import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useNavigator } from '@repo/toolkit/use-navigator'

import Typography, { TypographyProps } from './typography'

interface FormattedTextProps {
  text: string
  variant?: TypographyProps['variant']
  boldVariant?: TypographyProps['variant']
  className?: string
  linkClassName?: string
  linkTarget?: '_blank' | '_self' | '_parent' | '_top'
}

/**
 * FormattedText - A versatile text component that supports HTML formatting
 *
 * @description
 * This component parses text with HTML syntax and renders it with proper Typography components.
 * It supports bold text, external links, and internal navigation links with distinct tags.
 *
 * @example
 * ```tsx
 * // Simple bold text
 * <FormattedText text="This is <strong>bold</strong> text" />
 * <FormattedText text="This is <b>bold</b> text" />
 *
 * // External link (opens in new tab)
 * <FormattedText text='Visit <a href="https://example.com">our website</a>' />
 *
 * // Internal navigation (uses React Router)
 * <FormattedText text='Go to <route href="/dashboard">dashboard</route>' />
 * <FormattedText text='View <route href="../profile">user profile</route>' />
 *
 * // Combined formatting
 * <FormattedText text='<strong>Important</strong>: Please visit <a href="https://docs.example.com">our docs</a> for more info' />
 *
 * // Mixed internal and external links
 * <FormattedText text='Check <route href="/dashboard">dashboard</route> or visit <a href="https://github.com">GitHub</a> for <strong>updates</strong>' />
 *
 * // Custom variants
 * <FormattedText
 *   text='Tu as <strong>60 minutes maximum</strong> pour <route href="/test">commencer le test</route>'
 *   variant="secondary-body"
 *   boldVariant="tag"
 *   linkClassName="text-blue-600 hover:text-blue-800 underline"
 * />
 * ```
 *
 * @param text - The text to format with <strong>, <b>, <route>, and <a> HTML tags
 * @param variant - Typography variant for normal text (default: 'tag')
 * @param boldVariant - Typography variant for bold text (default: 'tag')
 * @param className - Additional CSS classes for the container
 * @param linkClassName - CSS classes for links (default: 'text-blue-600 hover:text-blue-800 underline')
 * @param linkTarget - Target attribute for external links (default: '_blank')
 */
const FormattedText = ({
  text,
  variant = 'tag',
  boldVariant = 'tag',
  className,
  linkClassName = 'text-blue-600 hover:text-blue-800 underline',
  linkTarget = '_blank'
}: FormattedTextProps) => {
  const { navigate } = useNavigator()

  const parseTextWithFormatting = (text: string) => {
    // Combined regex to match HTML tags: <strong>, <b>, <route>, and <a>
    const htmlRegex =
      /(<strong>.*?<\/strong>|<b>.*?<\/b>|<route\s+href="[^"]*"[^>]*>.*?<\/route>|<a\s+href="[^"]*"[^>]*>.*?<\/a>)/g
    const parts = text.split(htmlRegex)

    return parts.map((part, index) => {
      // Handle <strong> tags
      if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
        const boldText = part.slice(8, -9) // Remove <strong> and </strong>
        return (
          <Typography variant={boldVariant} key={index} className="inline font-bold">
            {boldText}
          </Typography>
        )
      }

      // Handle <b> tags
      if (part.startsWith('<b>') && part.endsWith('</b>')) {
        const boldText = part.slice(3, -4) // Remove <b> and </b>
        return (
          <Typography variant={boldVariant} key={index} className="inline font-bold">
            {boldText}
          </Typography>
        )
      }

      // Handle <route> tags (internal links)
      if (part.startsWith('<route') && part.includes('href="') && part.endsWith('</route>')) {
        const routeMatch = part.match(/<route\s+href="([^"]*)"[^>]*>(.*?)<\/route>/)
        if (routeMatch) {
          const [, routeUrl, routeText] = routeMatch

          return (
            <Typography.Link
              key={index}
              variant="tag"
              className={cn(linkClassName, 'inline')}
              onClick={() => navigate(routeUrl)}
            >
              {routeText}
            </Typography.Link>
          )
        }
      }

      // Handle <a> tags (external links)
      if (part.startsWith('<a') && part.includes('href="') && part.endsWith('</a>')) {
        const linkMatch = part.match(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/)
        if (linkMatch) {
          const [, linkUrl, linkText] = linkMatch

          return (
            <a
              key={index}
              href={linkUrl}
              target={linkTarget}
              rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
              className={linkClassName}
            >
              {linkText}
            </a>
          )
        }
      }

      return part
    })
  }

  return (
    <Typography variant={variant} className={className}>
      {parseTextWithFormatting(text)}
    </Typography>
  )
}

export default FormattedText
